import { takeLatest, put, all, call } from 'redux-saga/effects';
import { enums } from 'console-kit';

import ConsoleAppActions from '../../App/redux/app-actions';
import RulesetActions from './ruleset-actions';
import RulesetApi from './ruleset-api';

/** Fetches rulesets. */
function* fetchRulesetsHandler() {
  const response = yield call(RulesetApi.fetchRulesets);

  if (response.error) {
    yield put(RulesetActions.fetchRulesetsFailure(response.error));
  } else {
    yield put(RulesetActions.fetchRulesetsSuccess(response));
  }
}

/** Clones a ruleset. */
function* cloneRulesetHandler({ payload: { rulesetId, rulesetName } }) {
  const { error } = yield call(RulesetApi.cloneRuleset, {
    id:   rulesetId,
    name: rulesetName,
  });

  if (error) {
    yield put(RulesetActions.cloneRulesetFailure(error));
  } else {
    yield put(RulesetActions.cloneRulesetSuccess());
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.SUCCESS,
        header:      'success',
        byline:      `The ruleset "${rulesetName}" has been cloned`,
      })
    );

    yield put(RulesetActions.fetchRulesets());
  }
}

/** Creates a ruleset. */
function* createRulesetHandler({ payload: { ruleset } }) {
  const { error } = yield call(RulesetApi.createRuleset, ruleset);

  if (error) {
    yield put(RulesetActions.createRulesetFailure(error));
  } else {
    yield put(RulesetActions.createRulesetSuccess(ruleset));
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.SUCCESS,
        header:      'success',
        byline:      `The ruleset "${ruleset.name}" has been created`,
      })
    );

    yield put(RulesetActions.fetchRulesets());
  }
}

/** Updates a ruleset. */
function* updateRulesetHandler({ payload: { ruleset } }) {
  const { error } = yield call(RulesetApi.updateRuleset, ruleset);

  if (error) {
    yield put(RulesetActions.updateRulesetFailure(error));
  } else {
    yield put(RulesetActions.updateRulesetSuccess(ruleset));
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.SUCCESS,
        header:      'success',
        byline:      `The ruleset "${ruleset.name}" has been updated`,
      })
    );

    yield put(RulesetActions.fetchRulesets());
  }
}

/** Deletes selected rulesets. */
function* deleteRulesetsHandler({ payload: { ids } }) {
  const { error, customError } = yield call(RulesetApi.deleteRulesets, { ids });
  const selectedIdsCount = ids.length;

  if (error) yield put(RulesetActions.deleteRulesetsApiFailure(error)); //default API error handle

  if (customError) {
    const idsNotDeleted = customError.map((errorEntity) => parseInt(errorEntity.id, 10));
    const idsDeleted = ids.filter((id) => !idsNotDeleted.includes(id));

    yield all([
      yield put(
        ConsoleAppActions.setAlertMessage({
          messageType: enums.messageTypes.ERROR,
          header:      'error',
          byline:      `Failed to delete ${idsNotDeleted.length} of ${selectedIdsCount} ruleset(s)`,
        })
      ),

      yield put(RulesetActions.deleteRulesetsPartialFailure(idsNotDeleted, idsDeleted)),
    ]);
  }

  if (error || customError) {
    yield put(RulesetActions.deleteRulesetsFailure());
  } else {
    //else the success path
    yield all([
      yield put(RulesetActions.deleteRulesetsSuccess(ids)),
      yield put(
        ConsoleAppActions.setAlertMessage({
          messageType: enums.messageTypes.SUCCESS,
          header:      'success',
          byline:      `Successfully deleted ${selectedIdsCount} ruleset(s)`,
        })
      ),
    ]);
  }

  yield put(RulesetActions.fetchRulesets());
}

function* rulesetsSagas() {
  yield all([
    takeLatest(RulesetActions.fetchRulesets.getType(), fetchRulesetsHandler),
    takeLatest(RulesetActions.cloneRuleset.getType(), cloneRulesetHandler),
    takeLatest(RulesetActions.createRuleset.getType(), createRulesetHandler),
    takeLatest(RulesetActions.updateRuleset.getType(), updateRulesetHandler),
    takeLatest(RulesetActions.deleteRulesets.getType(), deleteRulesetsHandler),
  ]);
}

export default rulesetsSagas;
